import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AdminApi } from "../api/admin-api";
import { CreateApiKeyDto } from "@repo/api/user";
import { toast } from "sonner";

interface CreateApiKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (apiKey: string) => void;
}

export default function CreateApiKeyDialog({
  isOpen,
  onClose,
  onSuccess,
}: CreateApiKeyDialogProps) {
  const [name, setName] = useState("");
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async (data: CreateApiKeyDto) => {
      return AdminApi.createApiKey(data);
    },
    onSuccess: (data) => {
      setCreatedApiKey(data.apiKey);
      toast.success("API Key created successfully!");
      onSuccess?.(data.apiKey);
    },
    onError: (error) => {
      console.error("Failed to create API key:", error);
      toast.error("Failed to create API key. Please try again.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({ name: name.trim() || undefined });
  };

  const handleClose = () => {
    setName("");
    setCreatedApiKey(null);
    onClose();
  };

  const copyToClipboard = async () => {
    if (createdApiKey) {
      try {
        await navigator.clipboard.writeText(createdApiKey);
        toast.success("API Key copied to clipboard!");
      } catch (error) {
        console.error("Failed to copy:", error);
        toast.error("Failed to copy API key");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {createdApiKey ? "API Key Created" : "Create API Key"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {createdApiKey ? (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <div className="flex">
                <div className="text-yellow-400 text-xl mr-3">⚠️</div>
                <div>
                  <p className="text-yellow-700 font-medium">Important!</p>
                  <p className="text-yellow-600 text-sm mt-1">
                    This API key will only be shown once. Make sure to copy it
                    now.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Your API Key
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={createdApiKey}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                API Key Name (Optional)
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Production API Key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500">
                Give your API key a descriptive name to help you identify it
                later.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createMutation.isPending ? "Creating..." : "Create API Key"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
